import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Button, Input, InputNumber, Select, Modal, Form, Space, Tag, Typography, message,
    Popconfirm, Drawer, DatePicker, Tooltip, Tabs, Switch, Collapse, Radio,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, TagOutlined, HistoryOutlined, SendOutlined, StopOutlined,
    EyeOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import couponService from '../../services/couponService';
import { formatMoney } from '../../../shared/utils/money';
import { useTranslation } from '../../../shared/i18n';
import CouponCampaigns from './CouponCampaigns';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS = { active: 'green', disabled: 'red', available: 'green', used: 'blue', expired: 'default', revoked: 'red',
    converted: 'green', downgraded: 'orange', cancelled: 'default' };
const ids = (v) => (Array.isArray(v) ? v : String(v || '').split(/[\s,]+/)).map((x) => parseInt(x, 10)).filter((x) => x > 0);

/** 优惠码 + 券包 + 自动发券规则 + 0 元体验（admin API 独立于 user API）。 */
const Coupons = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('coupons');
    const [productTypes, setProductTypes] = useState([]);
    const [products, setProducts] = useState([]);
    const [allCoupons, setAllCoupons] = useState([]); // 发券/规则下拉

    useEffect(() => {
        // 'llm' 不是产品类型：LLM 充值券（划转 LLM 额度时多送额度，见 api LlmTransferService）
        couponService.getProductTypes().then((r) => r.success && setProductTypes([...(r.data || []), { code: 'llm', name: 'LLM API 充值' }])).catch(() => {});
        couponService.getProducts().then((r) => r.success && setProducts(r.data || [])).catch(() => {});
    }, []);
    const reloadAllCoupons = useCallback(() => {
        couponService.getCoupons({ page: 1, limit: 100 }).then((r) => r.success && setAllCoupons(r.data || [])).catch(() => {});
    }, []);
    useEffect(() => { reloadAllCoupons(); }, [reloadAllCoupons]);

    // 按产品线分组（套餐名 + #id），下架/隐藏的标出来
    const productOptions = Object.values(products.reduce((g, p) => {
        const k = p.product_type_code || 'other';
        if (!g[k]) g[k] = { code: k, label: p.product_type_name || k, options: [] };
        g[k].options.push({ value: p.id, label: `${p.name} #${p.id}${p.status && p.status !== 'active' ? ` (${t(p.status === 'hidden' ? 'Hidden' : 'Disabled')})` : ''}` });
        return g;
    }, {}));
    const productNames = Object.fromEntries(products.map((p) => [p.id, p.name]));
    const couponOptions = allCoupons.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}${c.status !== 'active' ? ` (${t('Disabled')})` : ''}` }));

    return (
        <Card title={<span><TagOutlined /> {t('Coupons')}</span>}>
            <Tabs
                activeKey={tab}
                onChange={setTab}
                destroyInactiveTabPane
                items={[
                    { key: 'coupons', label: t('Coupons'), children: <CouponList t={t} productTypes={productTypes} productOptions={productOptions} productNames={productNames} onChanged={reloadAllCoupons} /> },
                    { key: 'issued', label: t('Issued'), children: <IssuedList t={t} couponOptions={couponOptions} productTypes={productTypes} /> },
                    { key: 'campaigns', label: t('Campaigns'), children: <CouponCampaigns t={t} couponOptions={couponOptions} productTypes={productTypes} onChanged={reloadAllCoupons} /> },
                    { key: 'rules', label: t('Auto-issue rules'), children: <RuleList t={t} couponOptions={couponOptions} productOptions={productOptions} onChanged={reloadAllCoupons} /> },
                    { key: 'trials', label: t('Free trials'), children: <TrialList t={t} /> },
                ]}
            />
        </Card>
    );
};

const CYCLE_LABEL = { monthly: 'Monthly', quarterly: 'Quarterly', semi_annually: 'Semi-annually', annually: 'Annually', biennially: 'Biennially', triennially: 'Triennially' };

/** 自动发券触发条件 + 一句话说明（顺序 = 后台下拉顺序） */
const TRIGGERS = [
    { value: 'register', label: 'Registration', desc: 'New user verifies their email (issued right away)' },
    { value: 'cdn_usage', label: 'CDN usage', desc: 'CDN traffic passes a threshold (checked every 5 minutes)' },
    { value: 'cdn_domain_added', label: 'First CDN domain', desc: 'User adds their first CDN domain (issued right away)' },
    { value: 'cdn_domain_active', label: 'CDN domain connected', desc: 'A CDN domain gets traffic or a certificate (checked every 5 minutes)' },
    { value: 'first_paid_order', label: 'First paid order', desc: 'User pays their first order (issued right away)' },
    { value: 'llm_activated', label: 'LLM activated', desc: 'User activates LLM API (issued right away)' },
];
const EVENT_TRIGGERS = ['cdn_domain_added', 'cdn_domain_active', 'first_paid_order', 'llm_activated'];

const discountText = (r, t) => {
    if (r.type === 'trial') return t('Free trial ({{cycle}})', { cycle: r.trial_cycle || 'monthly' });
    if (r.type === 'percent') {
        return t('{{value}}% off', { value: Number(r.value) }) + (r.max_discount != null ? ` · ${t('cap {{v}}', { v: formatMoney(r.max_discount, r.currency) })}` : '');
    }
    return t('{{value}} off', { value: formatMoney(r.value, r.currency) });
};
const renewalText = (r, t) => {
    if (r.type !== 'percent' || !r.renewal_mode || r.renewal_mode === 'first') return t('First invoice');
    if (r.renewal_mode === 'forever') return t('Forever');
    return t('First {{n}} periods', { n: r.renewal_periods });
};

// ------------------------------------------------------------------ coupons
const CouponList = ({ t, productTypes, productOptions, productNames = {}, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [coupons, setCoupons] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [stats, setStats] = useState({});
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [issueFor, setIssueFor] = useState(null);
    const [form] = Form.useForm();
    const couponType = Form.useWatch('type', form);
    const renewalMode = Form.useWatch('renewal_mode', form);
    const appliesTo = Form.useWatch('applies_to', form);
    const [drawerCoupon, setDrawerCoupon] = useState(null);
    const [redemptions, setRedemptions] = useState([]);
    const [redemptionsTotal, setRedemptionsTotal] = useState(0);
    const [redemptionsPage, setRedemptionsPage] = useState(1);
    const [redemptionsLoading, setRedemptionsLoading] = useState(false);

    const load = useCallback(async (p = page, l = limit, s = search, st = statusFilter) => {
        setLoading(true);
        try {
            const params = { page: p, limit: l };
            if (s) params.search = s;
            if (st) params.status = st;
            const [res, st2] = await Promise.all([couponService.getCoupons(params), couponService.getStats()]);
            if (res.success) {
                setCoupons(res.data || []);
                setTotal(res.total || 0);
            }
            if (st2.success) setStats(st2.data || {});
        } catch (e) {
            message.error(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, statusFilter]);

    useEffect(() => { load(); }, [page, limit, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ type: 'percent', status: 'active', per_user_limit: 1, visibility: 'public', renewal_mode: 'first', trial_cycle: 'monthly' });
        setModalVisible(true);
    };
    const openEdit = (r) => {
        setEditing(r);
        form.resetFields();
        form.setFieldsValue({
            code: r.code, name: r.name, description: r.description, type: r.type, value: Number(r.value),
            min_amount: r.min_amount != null ? Number(r.min_amount) : null,
            max_discount: r.max_discount != null ? Number(r.max_discount) : null,
            max_uses: r.max_uses, per_user_limit: r.per_user_limit, applies_to: r.applies_to || undefined,
            product_ids: r.product_ids || [], visibility: r.visibility || 'public',
            renewal_mode: r.renewal_mode || 'first', renewal_periods: r.renewal_periods, trial_cycle: r.trial_cycle || 'monthly',
            valid_days: r.valid_days, max_items: r.max_items ?? null, cycles: r.cycles || [],
            validity: (r.valid_from || r.valid_until) ? [r.valid_from ? dayjs(r.valid_from) : null, r.valid_until ? dayjs(r.valid_until) : null] : undefined,
            status: r.status,
        });
        setModalVisible(true);
    };

    const submit = async () => {
        try {
            const v = await form.validateFields();
            const [from, until] = v.validity || [null, null];
            const payload = {
                code: v.code, name: v.name, description: v.description || null, type: v.type,
                value: v.type === 'trial' ? 100 : v.value,
                min_amount: v.min_amount ?? null,
                max_discount: v.type === 'percent' ? (v.max_discount ?? null) : null,
                max_uses: v.max_uses ?? null, per_user_limit: v.per_user_limit ?? 1,
                applies_to: v.applies_to || null, product_ids: v.product_ids || [],
                visibility: v.visibility, renewal_mode: v.type === 'percent' ? v.renewal_mode : 'first',
                renewal_periods: v.renewal_periods ?? null, trial_cycle: v.trial_cycle || 'monthly',
                valid_days: v.valid_days ?? null,
                max_items: v.type === 'trial' ? null : (v.max_items ?? null), cycles: v.cycles || [],
                valid_from: from ? from.format('YYYY-MM-DD HH:mm:ss') : null,
                valid_until: until ? until.format('YYYY-MM-DD HH:mm:ss') : null,
                status: v.status,
            };
            setSubmitting(true);
            const res = editing ? await couponService.updateCoupon({ id: editing.id, ...payload }) : await couponService.createCoupon(payload);
            if (res.success) {
                message.success(t('Saved'));
                setModalVisible(false);
                load();
                onChanged();
            } else {
                message.error(res.error || t('Save failed'));
            }
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e.response?.data?.error || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (r) => {
        try {
            const res = await couponService.deleteCoupon(r.id);
            res.success ? message.success(res.message || t('Deleted')) : message.error(res.error);
            load();
            onChanged();
        } catch (e) {
            message.error(e.response?.data?.error || e.message);
        }
    };

    const loadRedemptions = async (couponId, p = 1) => {
        setRedemptionsLoading(true);
        try {
            const res = await couponService.getRedemptions(couponId, { page: p, limit: 20 });
            if (res.success) {
                setRedemptions(res.data || []);
                setRedemptionsTotal(res.total || 0);
            }
        } finally {
            setRedemptionsLoading(false);
        }
    };

    const columns = [
        { title: t('Code'), dataIndex: 'code', render: (code, r) => <Space direction="vertical" size={0}><Text copyable strong>{code}</Text>{r.visibility === 'wallet' && <Tag color="purple">{t('Wallet only')}</Tag>}</Space> },
        { title: t('Name'), dataIndex: 'name', ellipsis: true },
        { title: t('Discount'), key: 'discount', render: (_, r) => discountText(r, t) },
        { title: t('Renewals'), key: 'renewal', render: (_, r) => renewalText(r, t) },
        {
            title: t('Scope'), key: 'scope', render: (_, r) => {
                const extra = [
                    r.max_items ? <Tag key="mi">{t('Up to {{n}} items', { n: r.max_items })}</Tag> : null,
                    r.cycles?.length ? <Tag key="cy">{r.cycles.map((c) => t(CYCLE_LABEL[c] || c)).join(' / ')}</Tag> : null,
                ];
                if (r.product_ids?.length) return [...r.product_ids.map((id) => <Tag key={id} color="geekblue">{productNames[id] || `#${id}`}</Tag>), ...extra];
                if (!r.applies_to) return [<Tag key="all">{t('All')}</Tag>, ...extra];
                const pt = productTypes.find((x) => x.code === r.applies_to);
                return [<Tag key="pt" color="blue">{pt ? pt.name : r.applies_to}</Tag>, ...extra];
            },
        },
        {
            title: t('Usage'), key: 'usage', render: (_, r) => {
                const s = stats[r.id] || {};
                const w = s.wallet || {};
                const red = s.redemptions?.checkout || {};
                return (
                    <Tooltip title={`${t('Issued')}: ${Object.values(w).reduce((a, b) => a + b, 0)} · ${t('Available')}: ${w.available || 0} · ${t('Expired')}: ${w.expired || 0} · ${t('Discount total')}: ${formatMoney(red.discount || 0, r.currency)}${s.redemptions?.renewal ? ` · ${t('Renewals')}: ${s.redemptions.renewal.uses}` : ''}${s.trials ? ` · ${t('Trials')}: ${JSON.stringify(s.trials)}` : ''}`}>
                        <Text>{r.used_count} / {r.max_uses ?? '∞'}</Text>
                    </Tooltip>
                );
            },
        },
        {
            title: t('Validity'), key: 'validity', render: (_, r) => {
                if (!r.valid_from && !r.valid_until && !r.valid_days) return t('No limit');
                const fmt = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '…');
                return <div style={{ fontSize: 12 }}>{(r.valid_from || r.valid_until) && <div>{fmt(r.valid_from)} ~ {fmt(r.valid_until)}</div>}{r.valid_days && <div>{t('{{n}} days after issue', { n: r.valid_days })}</div>}</div>;
            },
        },
        { title: t('Status'), dataIndex: 'status', render: (s) => <Tag color={STATUS_COLORS[s]}>{t(s === 'active' ? 'Active' : 'Disabled')}</Tag> },
        {
            title: t('Actions'), key: 'action', width: 260, render: (_, r) => (
                <Space size={0}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>{t('Edit')}</Button>
                    <Button type="link" size="small" icon={<SendOutlined />} disabled={r.status !== 'active'} onClick={() => setIssueFor(r)}>{t('Issue')}</Button>
                    <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => { setDrawerCoupon(r); setRedemptionsPage(1); loadRedemptions(r.id, 1); }}>{t('Redemptions')}</Button>
                    <Popconfirm title={t('Delete this coupon?')} description={t('Coupons that were used or issued are disabled instead')} onConfirm={() => remove(r)}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <Input.Search placeholder={t('Search code / name')} allowClear style={{ width: 220 }} value={search}
                    onChange={(e) => setSearch(e.target.value)} onSearch={() => { setPage(1); load(1, limit, search, statusFilter); }} />
                <Select placeholder={t('Status')} allowClear style={{ width: 120 }} value={statusFilter}
                    onChange={(v) => { setStatusFilter(v || null); setPage(1); }}
                    options={[{ value: 'active', label: t('Active') }, { value: 'disabled', label: t('Disabled') }]} />
                <Button icon={<ReloadOutlined />} onClick={() => load()} />
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('New coupon')}</Button>
            </Space>
            <Table columns={columns} dataSource={coupons} rowKey="id" loading={loading} scroll={{ x: 'max-content' }}
                pagination={{ current: page, pageSize: limit, total, showSizeChanger: true, onChange: (p, l) => { setPage(p); setLimit(l); } }} />

            <Modal title={editing ? `${t('Edit coupon')} · ${editing.code}` : t('New coupon')} open={modalVisible}
                onCancel={() => setModalVisible(false)} onOk={submit} confirmLoading={submitting} width={680} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Space style={{ display: 'flex' }} align="start" wrap>
                        <Form.Item name="code" label={t('Code')} style={{ width: 200 }}
                            rules={[{ required: true }, { pattern: /^[A-Za-z0-9_-]{2,50}$/, message: 'A-Z 0-9 _ -' }]}>
                            <Input onChange={(e) => form.setFieldValue('code', e.target.value.toUpperCase())} />
                        </Form.Item>
                        <Form.Item name="name" label={t('Name')} style={{ width: 240 }} rules={[{ required: true }]}>
                            <Input maxLength={100} />
                        </Form.Item>
                        <Form.Item name="status" label={t('Status')} style={{ width: 140 }}>
                            <Select options={[{ value: 'active', label: t('Active') }, { value: 'disabled', label: t('Disabled') }]} />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }} align="start" wrap>
                        <Form.Item name="type" label={t('Type')} style={{ width: 200 }}>
                            <Select options={[
                                { value: 'percent', label: t('Percent off') },
                                { value: 'fixed', label: t('Amount off') },
                                { value: 'trial', label: t('Free trial of a plan') },
                            ]} />
                        </Form.Item>
                        {couponType !== 'trial' && (
                            <Form.Item name="value" label={couponType === 'percent' ? '%' : t('Amount')} style={{ width: 140 }} rules={[{ required: true }]}>
                                <InputNumber min={0.01} max={couponType === 'percent' ? 100 : undefined} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        )}
                        {couponType === 'percent' && (
                            <Form.Item name="renewal_mode" label={t('Renewals')} style={{ width: 170 }}>
                                <Select options={[
                                    { value: 'first', label: t('First invoice') },
                                    { value: 'periods', label: t('First N periods') },
                                    { value: 'forever', label: t('Forever') },
                                ]} />
                            </Form.Item>
                        )}
                        {couponType === 'percent' && renewalMode === 'periods' && (
                            <Form.Item name="renewal_periods" label="N" style={{ width: 90 }} rules={[{ required: true }]} tooltip={t('Including the first invoice')}>
                                <InputNumber min={2} precision={0} style={{ width: '100%' }} />
                            </Form.Item>
                        )}
                        {couponType === 'trial' && (
                            <Form.Item name="trial_cycle" label={t('Trial period')} style={{ width: 160 }}>
                                <Select options={[{ value: 'monthly', label: t('1 month') }, { value: 'quarterly', label: t('1 quarter') }]} />
                            </Form.Item>
                        )}
                    </Space>
                    <Space style={{ display: 'flex' }} align="start" wrap>
                        <Form.Item name="applies_to" label={t('Product line')} style={{ width: 200 }}>
                            <Select allowClear placeholder={t('All')} options={productTypes.map((x) => ({ value: x.code, label: x.name }))} />
                        </Form.Item>
                        <Form.Item name="product_ids" label={couponType === 'trial' ? t('Plan') : t('Products')} style={{ width: 400 }}
                            rules={couponType === 'trial' ? [{ required: true }] : []}>
                            <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder={t('Any')}
                                options={appliesTo ? productOptions.filter((g) => g.code === appliesTo) : productOptions} />
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }} align="start" wrap>
                        <Form.Item name="visibility" label={t('Who can use it')} style={{ width: 200 }} tooltip={t('Wallet only: issued to users, cannot be typed in')}>
                            <Radio.Group optionType="button" options={[{ value: 'public', label: t('Code') }, { value: 'wallet', label: t('Wallet only') }]} />
                        </Form.Item>
                        <Form.Item name="valid_days" label={t('Expires after issue (days)')} style={{ width: 200 }}>
                            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder={t('No limit')} />
                        </Form.Item>
                    </Space>
                    <Collapse ghost items={[{
                        key: 'adv', label: t('Advanced'), children: (
                            <>
                                <Space style={{ display: 'flex' }} align="start" wrap>
                                    <Form.Item name="min_amount" label={t('Minimum spend')} style={{ width: 150 }}>
                                        <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                    </Form.Item>
                                    {couponType === 'percent' && (
                                        <Form.Item name="max_discount" label={t('Max discount')} style={{ width: 150 }}>
                                            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                        </Form.Item>
                                    )}
                                    <Form.Item name="max_uses" label={t('Total uses')} style={{ width: 130 }}>
                                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name="per_user_limit" label={t('Per user')} style={{ width: 110 }} tooltip={t('0 = unlimited')}>
                                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Space>
                                <Space style={{ display: 'flex' }} align="start" wrap>
                                    {couponType !== 'trial' && (
                                        <Form.Item name="max_items" label={t('Max items per order')} style={{ width: 170 }}
                                            tooltip={t('Discount at most this many units per checkout, highest priced first')}>
                                            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder={t('No limit')} />
                                        </Form.Item>
                                    )}
                                    <Form.Item name="cycles" label={t('Billing cycles')} style={{ width: 300 }}>
                                        <Select mode="multiple" allowClear placeholder={t('Any')}
                                            options={Object.entries(CYCLE_LABEL).map(([value, label]) => ({ value, label: t(label) }))} />
                                    </Form.Item>
                                </Space>
                                <Form.Item name="validity" label={t('Valid between')}>
                                    <RangePicker showTime allowEmpty={[true, true]} format="YYYY-MM-DD HH:mm" />
                                </Form.Item>
                                <Form.Item name="description" label={t('Description')}>
                                    <Input maxLength={255} />
                                </Form.Item>
                            </>
                        ),
                    }]} />
                </Form>
            </Modal>

            <IssueModal t={t} coupon={issueFor} productTypes={productTypes} onClose={() => { setIssueFor(null); load(); }} />

            <Drawer title={drawerCoupon ? `${t('Redemptions')} · ${drawerCoupon.code}` : ''} open={!!drawerCoupon} onClose={() => setDrawerCoupon(null)} width={720}>
                <Table rowKey="id" size="small" loading={redemptionsLoading} dataSource={redemptions} scroll={{ x: 'max-content' }}
                    columns={[
                        { title: t('User'), key: 'user', render: (_, r) => <div><div>{r.username || `#${r.user_id}`}</div>{r.email && <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>}</div> },
                        { title: t('Invoice'), key: 'invoice', render: (_, r) => (r.invoice_id ? <div>{r.invoice_number || `#${r.invoice_id}`} {r.invoice_status && <Tag>{r.invoice_status}</Tag>}</div> : '-') },
                        { title: t('Discount'), dataIndex: 'discount_amount', render: (v, r) => <Text type="success">{formatMoney(v, r.currency, { negative: true })}</Text> },
                        { title: t('Time'), dataIndex: 'created_at', render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-') },
                    ]}
                    pagination={{ current: redemptionsPage, pageSize: 20, total: redemptionsTotal, onChange: (p) => { setRedemptionsPage(p); loadRedemptions(drawerCoupon.id, p); } }} />
            </Drawer>
        </>
    );
};

// ------------------------------------------------------------------ manual issue
const IssueModal = ({ t, coupon, productTypes, onClose }) => {
    const [form] = Form.useForm();
    const target = Form.useWatch('target', form);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (coupon) {
            form.resetFields();
            form.setFieldsValue({ target: 'users', once_per_user: true, notify: true });
            setPreview(null);
        }
    }, [coupon, form]);

    const payload = (v) => {
        const p = { coupon_id: coupon.id, once_per_user: v.once_per_user, notify: v.notify, note: v.note || null };
        if (v.target === 'users') {
            p.user_ids = ids(v.user_ids);
        } else {
            const [a, b] = v.registered || [null, null];
            p.filter = {
                all: v.target === 'all',
                registered_after: a ? a.format('YYYY-MM-DD') : null,
                registered_before: b ? b.format('YYYY-MM-DD 23:59:59') : null,
                email_verified: !!v.email_verified,
                product_type: v.product_type || null,
                no_product_type: v.no_product_type || null,
            };
        }
        return p;
    };

    const run = async (dry) => {
        const v = await form.validateFields();
        setBusy(true);
        try {
            const r = await couponService.issue({ ...payload(v), dry_run: dry });
            if (!r.success) {
                message.error(r.error);
            } else if (dry) {
                setPreview(r.data.users);
            } else {
                message.success(t('Issued {{n}}, skipped {{m}}', { n: r.data.issued, m: r.data.skipped }));
                onClose();
            }
        } catch (e) {
            message.error(e.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal title={coupon ? `${t('Issue')} · ${coupon.code}` : ''} open={!!coupon} onCancel={onClose} destroyOnClose width={560}
            footer={[
                target !== 'users' && <Button key="p" onClick={() => run(true)} loading={busy}>{preview == null ? t('Preview') : t('{{n}} users', { n: preview })}</Button>,
                <Button key="ok" type="primary" icon={<SendOutlined />} onClick={() => run(false)} loading={busy}>{t('Issue')}</Button>,
            ]}>
            <Form form={form} layout="vertical" onValuesChange={() => setPreview(null)}>
                <Form.Item name="target">
                    <Radio.Group optionType="button" options={[{ value: 'users', label: t('Users') }, { value: 'filter', label: t('Filter') }, { value: 'all', label: t('All users') }]} />
                </Form.Item>
                {target === 'users' ? (
                    <Form.Item name="user_ids" label={t('User IDs')} rules={[{ required: true }]}>
                        <Input.TextArea rows={2} placeholder="12, 34, 56" />
                    </Form.Item>
                ) : (
                    <>
                        <Form.Item name="registered" label={t('Registered between')}><RangePicker allowEmpty={[true, true]} /></Form.Item>
                        <Space wrap>
                            <Form.Item name="product_type" label={t('Has product')} style={{ width: 200 }}>
                                <Select allowClear options={productTypes.map((x) => ({ value: x.code, label: x.name }))} />
                            </Form.Item>
                            <Form.Item name="no_product_type" label={t('Does not have')} style={{ width: 200 }}>
                                <Select allowClear options={productTypes.map((x) => ({ value: x.code, label: x.name }))} />
                            </Form.Item>
                            <Form.Item name="email_verified" label={t('Email verified')} valuePropName="checked"><Switch /></Form.Item>
                        </Space>
                    </>
                )}
                <Space wrap>
                    <Form.Item name="notify" label={t('Notify user')} valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="once_per_user" label={t('Once per user')} valuePropName="checked"><Switch /></Form.Item>
                </Space>
                <Form.Item name="note" label={t('Note')}><Input maxLength={255} /></Form.Item>
            </Form>
        </Modal>
    );
};

// ------------------------------------------------------------------ issued coupons
const IssuedList = ({ t, couponOptions }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [selected, setSelected] = useState([]);

    const load = useCallback(async (p = page, f = filters) => {
        setLoading(true);
        try {
            const r = await couponService.getIssued({ page: p, limit: 20, ...f });
            if (r.success) {
                setRows(r.data || []);
                setTotal(r.total || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [page, filters]);
    useEffect(() => { load(); }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const revoke = async (list) => {
        const r = await couponService.revoke(list);
        r.success ? message.success(t('Revoked {{n}}', { n: r.data.revoked })) : message.error(r.error);
        setSelected([]);
        load();
    };

    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <InputNumber placeholder={t('User ID')} style={{ width: 120 }} onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, user_id: v || undefined })); }} />
                <Input.Search placeholder={t('User / email / code')} allowClear style={{ width: 200 }} onSearch={(v) => { setPage(1); setFilters((f) => ({ ...f, search: v || undefined })); }} />
                <Select placeholder={t('Coupon')} allowClear showSearch optionFilterProp="label" style={{ width: 220 }} options={couponOptions}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, coupon_id: v || undefined })); }} />
                <Select placeholder={t('Status')} allowClear style={{ width: 120 }}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, status: v || undefined })); }}
                    options={['available', 'used', 'expired', 'revoked'].map((s) => ({ value: s, label: t(s[0].toUpperCase() + s.slice(1)) }))} />
                <Popconfirm title={t('Revoke selected unused coupons?')} disabled={!selected.length} onConfirm={() => revoke(selected)}>
                    <Button danger icon={<StopOutlined />} disabled={!selected.length}>{t('Revoke')} ({selected.length})</Button>
                </Popconfirm>
                <Button icon={<ReloadOutlined />} onClick={() => load()} />
            </Space>
            <Table rowKey="id" loading={loading} dataSource={rows} scroll={{ x: 'max-content' }}
                rowSelection={{ selectedRowKeys: selected, onChange: setSelected, getCheckboxProps: (r) => ({ disabled: r.status !== 'available' }) }}
                pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
                columns={[
                    { title: t('User'), key: 'u', render: (_, r) => <div><div>{r.username || `#${r.user_id}`} <Text type="secondary">#{r.user_id}</Text></div><Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text></div> },
                    { title: t('Coupon'), key: 'c', render: (_, r) => <div><Text strong>{r.code}</Text><div style={{ fontSize: 12 }}>{r.coupon_name}</div></div> },
                    { title: t('Source'), dataIndex: 'source', render: (v) => <Tag>{v}</Tag> },
                    { title: t('Status'), dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v]}>{t(v[0].toUpperCase() + v.slice(1))}</Tag> },
                    { title: t('Issued'), dataIndex: 'created_at', render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
                    { title: t('Expires'), dataIndex: 'expires_at', render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
                    { title: t('Invoice'), key: 'i', render: (_, r) => r.invoice_number || (r.invoice_id ? `#${r.invoice_id}` : '-') },
                    {
                        title: '', key: 'a', render: (_, r) => r.status === 'available' && (
                            <Popconfirm title={t('Revoke this coupon?')} onConfirm={() => revoke([r.id])}>
                                <Button type="link" size="small" danger>{t('Revoke')}</Button>
                            </Popconfirm>
                        ),
                    },
                ]} />
        </>
    );
};

// ------------------------------------------------------------------ rules
const RuleList = ({ t, couponOptions, productOptions, onChanged }) => {
    const [rows, setRows] = useState([]);
    const [jobs, setJobs] = useState([]); // 体验到期 / 券过期两个任务
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();
    const trigger = Form.useWatch('trigger', form);

    const load = async () => {
        setLoading(true);
        try {
            const r = await couponService.getRules();
            if (r.success) { setRows(r.data || []); setJobs(r.jobs || []); }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        const r = await couponService.saveRule(data);
        r.success ? message.success(t('Saved')) : message.error(r.error);
        load();
        onChanged();
        return r.success;
    };

    const open = (r) => {
        setEditing(r || {});
        form.resetFields();
        form.setFieldsValue(r ? {
            name: r.name, trigger: r.trigger, coupon_id: r.coupon_id, enabled: !!r.enabled,
            threshold_gb: r.params?.threshold_gb ?? null, quota_pct: r.params?.quota_pct ?? null, source_product_ids: r.params?.source_product_ids || [],
        } : { trigger: 'register', enabled: false });
    };

    const submit = async () => {
        const v = await form.validateFields();
        const ok = await save({
            id: editing?.id, name: v.name, trigger: v.trigger, coupon_id: v.coupon_id, enabled: v.enabled,
            params: v.trigger === 'cdn_usage' ? { threshold_gb: v.threshold_gb, quota_pct: v.quota_pct, source_product_ids: v.source_product_ids || [] } : {},
        });
        if (ok) setEditing(null);
    };

    const triggerText = (r) => {
        if (r.trigger === 'register') return t('New user (email verified)');
        if (r.trigger !== 'cdn_usage') return t(TRIGGERS.find((x) => x.value === r.trigger)?.label || r.trigger);
        return [r.params?.threshold_gb != null && t('CDN traffic ≥ {{n}} GB', { n: r.params.threshold_gb }),
            r.params?.quota_pct != null && t('≥ {{n}}% of plan traffic', { n: r.params.quota_pct })].filter(Boolean).join(' / ');
    };

    const [history, setHistory] = useState(null); // { job, rule_id?, title }
    const [preview, setPreview] = useState(null); // { rule, loading, data }
    const [running, setRunning] = useState(null);

    const doPreview = async (rule) => {
        setPreview({ rule, loading: true });
        const r = await couponService.previewRule(rule.id).catch((e) => ({ success: false, error: e.error || e.message }));
        if (!r.success) { message.error(r.error); setPreview(null); return; }
        setPreview({ rule, data: r.data });
    };
    // 立即运行前先取预览人数（事件规则 = 存量回填，可能很多人），放进确认框
    const [runCount, setRunCount] = useState({});
    const loadRunCount = (rule) => {
        setRunCount((m) => ({ ...m, [rule.id]: null }));
        couponService.previewRule(rule.id).then((r) => setRunCount((m) => ({ ...m, [rule.id]: r.success ? r.data.count : '?' })))
            .catch(() => setRunCount((m) => ({ ...m, [rule.id]: '?' })));
    };
    const doRun = async (rule) => {
        setRunning(rule.id);
        try {
            const r = await couponService.runRule(rule.id).catch((e) => ({ success: false, error: e.error || e.message }));
            r.success ? message.success(r.message) : message.error(r.error);
            load();
        } finally {
            setRunning(null);
        }
    };
    const ts = (v) => (v ? dayjs(v).format('MM-DD HH:mm') : '-');
    const gb = (b) => `${(Number(b || 0) / 1073741824).toFixed(2)} GB`;
    const jobName = { trials: t('CDN trial reminders/expiry'), wallet: t('Coupon expiry/release') };
    const statLabel = {
        started: t('Started'), reminded: t('Reminded'), converted: t('Converted'), downgraded: t('Back to free'),
        cancelled: t('Cancelled'), failed: t('Failed'), expired: t('Expired'), released: t('Released'),
    };
    const statsText = (s) => (s && Object.keys(s).length ? Object.entries(s).map(([k, v]) => `${statLabel[k] || k} ${v}`).join(' · ') : '-');

    return (
        <>
            <Space style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>{t('New rule')}</Button>
                <Button icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table rowKey="id" loading={loading} dataSource={rows} pagination={false}
                columns={[
                    { title: t('Name'), dataIndex: 'name' },
                    { title: t('When'), key: 'w', render: (_, r) => triggerText(r) },
                    { title: t('Coupon'), key: 'c', render: (_, r) => <span>{r.code} {r.coupon_status !== 'active' && <Tag>{t('Disabled')}</Tag>}</span> },
                    {
                        title: t('Enabled'), dataIndex: 'enabled', render: (v, r) => (
                            <Popconfirm title={v ? t('Stop issuing?') : t('Start issuing to real users? The coupon is enabled too.')} onConfirm={() => save({ id: r.id, enabled: !v })}>
                                <Switch checked={!!v} />
                            </Popconfirm>
                        ),
                    },
                    {
                        title: t('Last run'), key: 'lr', render: (_, r) => (
                            <Tooltip title={<>{t('Last checked')}: {ts(r.schedule?.last_checked_at)}<br />{t('Next run')}: {ts(r.schedule?.next_run_at)}</>}>
                                <Space size={4}>
                                    <span>{ts(r.schedule?.last_run_at)}</span>
                                    {r.schedule?.last_error && <Tooltip title={r.schedule.last_error}><Tag color="red">{t('Error')}</Tag></Tooltip>}
                                </Space>
                            </Tooltip>
                        ),
                    },
                    { title: t('Next run'), key: 'nr', render: (_, r) => ts(r.schedule?.next_run_at) },
                    {
                        title: <Tooltip title={t('Last run / today / total')}>{t('Issued')}</Tooltip>, key: 'is',
                        render: (_, r) => `${r.schedule?.issued_last_run ?? '-'} / ${r.schedule?.issued_today ?? 0} / ${r.schedule?.issued_total ?? r.issued_count}`,
                    },
                    { title: t('Skipped'), key: 'sk', render: (_, r) => r.schedule?.skipped ?? 0 },
                    {
                        title: '', key: 'a', render: (_, r) => (
                            <Space size={0}>
                                <Tooltip title={t('Preview')}><Button type="link" size="small" icon={<EyeOutlined />} onClick={() => doPreview(r)} /></Tooltip>
                                <Popconfirm title={t('Run this rule now?')} disabled={!r.enabled} onConfirm={() => doRun(r)}
                                    onOpenChange={(o) => o && loadRunCount(r)}
                                    description={runCount[r.id] == null ? t('Counting…')
                                        : `${t('Issues to {{n}} user(s)', { n: runCount[r.id] })}${EVENT_TRIGGERS.includes(r.trigger) ? ` · ${t('includes existing users')}` : ''}`}>
                                    <Tooltip title={r.enabled ? t('Run now') : t('Enable the rule first')}>
                                        <Button type="link" size="small" icon={<PlayCircleOutlined />} disabled={!r.enabled} loading={running === r.id} />
                                    </Tooltip>
                                </Popconfirm>
                                <Tooltip title={t('History')}><Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => setHistory({ job: 'rule', rule_id: r.id, title: r.name })} /></Tooltip>
                                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => open(r)} />
                            </Space>
                        ),
                    },
                ]} />
            <Table rowKey="job" size="small" style={{ marginTop: 16 }} dataSource={jobs} pagination={false}
                columns={[
                    { title: t('Job'), dataIndex: 'job', render: (v) => jobName[v] || v },
                    { title: t('Last run'), dataIndex: 'last_run_at', render: (v, r) => (
                        <Space size={4}>{ts(v)}{r.last_error && <Tooltip title={r.last_error}><Tag color="red">{t('Error')}</Tag></Tooltip>}</Space>) },
                    { title: t('Next run'), dataIndex: 'next_run_at', render: ts },
                    { title: t('Last result'), dataIndex: 'stats', render: statsText },
                    { title: '', key: 'a', render: (_, r) => <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => setHistory({ job: r.job, title: jobName[r.job] })} /> },
                ]} />
            <RunHistory t={t} target={history} onClose={() => setHistory(null)} statsText={statsText} />
            <Modal title={`${t('Preview')} · ${preview?.rule?.name || ''}`} open={!!preview} onCancel={() => setPreview(null)} footer={null} width={640}>
                {preview?.data && (
                    <>
                        <Text>{preview.data.backfill ? t('Run now would issue to {{n}} user(s)', { n: preview.data.count }) : t('Next run would issue to {{n}} user(s)', { n: preview.data.count })}</Text>
                        {preview.data.count > (preview.data.batch || 200) && <Text type="secondary"> ({t('{{n}} per run', { n: preview.data.batch })})</Text>}
                        {preview.data.reason === 'coupon_disabled' && <Tag style={{ marginLeft: 8 }}>{t('Coupon disabled')}</Tag>}
                    </>
                )}
                <Table rowKey="user_id" size="small" style={{ marginTop: 12 }} loading={preview?.loading} dataSource={preview?.data?.users || []}
                    pagination={false} scroll={{ y: 360 }}
                    columns={[
                        { title: 'ID', dataIndex: 'user_id', width: 100 },
                        { title: t('Email'), dataIndex: 'email' },
                        {
                            title: preview?.rule?.trigger === 'register' ? t('Registered') : (preview?.rule?.trigger === 'cdn_usage' ? t('Traffic') : t('Since')), key: 'm',
                            render: (_, u) => (preview?.rule?.trigger !== 'cdn_usage' ? ts(u.metric)
                                : <span>{gb(u.metric)}{u.metric_pct != null && <Text type="secondary"> · {u.metric_pct}%</Text>}</span>),
                        },
                    ]} />
            </Modal>
            <Modal title={editing?.id ? t('Edit rule') : t('New rule')} open={!!editing} onCancel={() => setEditing(null)} onOk={submit} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('Name')}><Input maxLength={100} /></Form.Item>
                    <Form.Item name="trigger" label={t('When')}>
                        <Select options={TRIGGERS.map((x) => ({ value: x.value, label: t(x.label) }))} />
                    </Form.Item>
                    {trigger && <Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16, fontSize: 12 }}>{t(TRIGGERS.find((x) => x.value === trigger)?.desc || '')}</Text>}
                    {trigger === 'cdn_usage' && (
                        <>
                            <Space wrap>
                                <Form.Item name="threshold_gb" label={t('Traffic ≥ (GB)')} style={{ width: 160 }}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                                <Form.Item name="quota_pct" label={t('or ≥ % of plan')} style={{ width: 160 }}><InputNumber min={1} max={100} style={{ width: '100%' }} /></Form.Item>
                            </Space>
                            <Form.Item name="source_product_ids" label={t('Only for plans')} tooltip={t('Empty = any CDN plan')}>
                                <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={productOptions} />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="coupon_id" label={t('Coupon')} rules={[{ required: true }]}>
                        <Select showSearch optionFilterProp="label" options={couponOptions} />
                    </Form.Item>
                    <Form.Item name="enabled" label={t('Enabled')} valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>
        </>
    );
};

// ------------------------------------------------------------------ rule / job run history
const RunHistory = ({ t, target, onClose, statsText }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    useEffect(() => { setPage(1); }, [target]);
    useEffect(() => {
        if (!target) return;
        setLoading(true);
        couponService.getRuleRuns({ job: target.job, rule_id: target.rule_id, page, limit: 20 }).then((r) => {
            if (r.success) { setRows(r.data || []); setTotal(r.total || 0); }
        }).finally(() => setLoading(false));
    }, [target, page]);
    const isRule = target?.job === 'rule';
    return (
        <Drawer title={`${t('History')} · ${target?.title || ''}`} open={!!target} onClose={onClose} width={720} destroyOnClose>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows}
                pagination={{ current: page, pageSize: 20, total, onChange: setPage, showSizeChanger: false }}
                columns={[
                    { title: t('Started'), dataIndex: 'started_at', render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss') },
                    { title: t('Source'), dataIndex: 'source', render: (v) => (v === 'manual' ? <Tag color="blue">{t('Manual')}</Tag> : v === 'event' ? <Tag color="green">{t('Event')}</Tag> : t('Scheduled')) },
                    ...(isRule ? [
                        { title: t('Candidates'), dataIndex: 'candidates' },
                        { title: t('Issued'), dataIndex: 'issued' },
                        { title: t('Skipped'), dataIndex: 'skipped' },
                    ] : [{ title: t('Result'), dataIndex: 'stats', render: statsText }]),
                    { title: 'ms', key: 'd', render: (_, r) => (r.finished_at ? Math.max(0, dayjs(r.finished_at).diff(dayjs(r.started_at))) : '-') },
                    { title: t('Error'), dataIndex: 'error', render: (v) => (v ? <Text type="danger" ellipsis={{ tooltip: v }} style={{ maxWidth: 200 }}>{v}</Text> : '') },
                ]} />
        </Drawer>
    );
};

// ------------------------------------------------------------------ trials
const TrialList = ({ t }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState();
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        couponService.getTrials({ page, limit: 20, status }).then((r) => {
            if (r.success) { setRows(r.data || []); setTotal(r.total || 0); }
        }).finally(() => setLoading(false));
    }, [page, status]);
    return (
        <>
            <Select placeholder={t('Status')} allowClear style={{ width: 160, marginBottom: 12 }} value={status} onChange={(v) => { setPage(1); setStatus(v); }}
                options={['active', 'converted', 'downgraded', 'cancelled'].map((s) => ({ value: s, label: t(s === 'downgraded' ? 'Back to free' : s[0].toUpperCase() + s.slice(1)) }))} />
            <Table rowKey="id" loading={loading} dataSource={rows} scroll={{ x: 'max-content' }}
                pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
                columns={[
                    { title: t('User'), key: 'u', render: (_, r) => <div>{r.username || `#${r.user_id}`} <Text type="secondary">#{r.user_id}</Text></div> },
                    { title: t('Plan'), dataIndex: 'product_name' },
                    { title: t('Coupon'), dataIndex: 'code' },
                    { title: t('Subscription'), dataIndex: 'subscription_id', render: (v) => `#${v}` },
                    { title: t('Trial ends'), dataIndex: 'trial_end', render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-') },
                    { title: t('Status'), dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v]}>{t(v[0].toUpperCase() + v.slice(1))}</Tag> },
                    { title: t('Reminders'), key: 'r', render: (_, r) => [r.reminded_3d_at && '3d', r.reminded_1d_at && '1d'].filter(Boolean).join(' · ') || '-' },
                ]} />
        </>
    );
};

export default Coupons;
